import type { TrackedTranslation, Values } from '@ps/web-core-intl';
import { useTrackers } from '@ps/web-shared-analytics';
import { InfoIcon } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import type { SyntheticEvent } from 'react';

import Prompt from '../Prompt/Prompt';

import { TXTCLI_Back, TXTCLI_OKX_got_it, TXTWCL_Okay } from './InfoPrompt.messages.json';
import { icon, large, rmfIcon, small, verifyIcon, withdrawLimit } from './InfoPrompt.scss';

interface Props {
  title: { translation: TrackedTranslation; values?: Values };
  iconWidth?: number;
  iconHeight?: number;
  variant?: 'default' | 'deposit' | 'switch' | 'wallet' | 'verification' | 'withdraw-limit' | 'rmf';
  hidePrompt?: boolean;
  shouldStopPropagation?: boolean;
  onCloseCallback?(): void;
}

const getTitle = (variant?: string) => {
  const title: Record<string, TrackedTranslation> = {
    deposit: TXTCLI_OKX_got_it,
    wallet: TXTCLI_Back
  };
  return (variant && title[variant]) || TXTWCL_Okay;
};

const InfoPrompt: React.FC<
  Props & { children: React.ReactNode | ((onCLose: unknown) => React.ReactNode) }
> = ({
  title,
  children,
  variant = 'default',
  hidePrompt = false,
  onCloseCallback,
  shouldStopPropagation
}) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const { buttonPressed } = useTrackers();
  const isDepositVariant = variant === 'deposit';
  const isSwitchVariant = variant === 'switch';
  const isVerificationVariant = variant === 'verification';
  const isWithdrawLimit = variant === 'withdraw-limit';
  const isRMFVariant = variant === 'rmf';

  const onDialogClick = (e: SyntheticEvent) => {
    e.stopPropagation();
  };

  const handleShowPrompt = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    buttonPressed({ buttonName: 'Info' });
    setShowPrompt(true);
  };

  const onClose = useCallback(() => {
    setShowPrompt(false);
    onCloseCallback?.();
  }, [onCloseCallback]);

  useEffect(() => {
    if (hidePrompt) {
      onClose();
    }
  }, [hidePrompt, onClose]);

  return (
    <>
      <InfoIcon
        data-testid="info-icon"
        className={classNames(icon, {
          [small]: isDepositVariant,
          [large]: isSwitchVariant,
          [verifyIcon]: isVerificationVariant,
          [withdrawLimit]: isWithdrawLimit,
          [rmfIcon]: isRMFVariant
        })}
        onClick={handleShowPrompt}
      />
      {showPrompt && !hidePrompt && (
        <Prompt
          title={title}
          variant="success"
          onClose={onClose}
          onPrimaryButtonClick={onClose}
          primaryButton={{ translation: getTitle(variant) }}
          {...(shouldStopPropagation && { onDialogClick })}
        >
          {typeof children === 'function' ? children(onClose) : children}
        </Prompt>
      )}
    </>
  );
};

export default InfoPrompt;
